import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClients } from '@/home/presentation/hooks/useClients';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import axios from '@/common/infrastructure/axios';
import { getUserIdFromSession } from '@/home/application/utils/sessionUtils';
import { ApiInvoiceItem, ApiSelectedInvoicesData } from '@/shopping-cart/application/types/SelectedInvoicesTypes';
import { fetchClientLogo } from '@/common/application/adapters/clientLogoService';
import { initPendoFromSession } from '@/common/application/utils/pendoSession';

interface CallerData {
  ContactID: number;
  ParentID: number;
  Title: string;
  LastName: string;
  FirstName: string;
  Phone: string;
  Email: string;
}

interface ClientContextType {
  selectedClientId: number | null;
  selectedClientPersonID: number | null;
  setSelectedClientPersonID: (clientPersonID: number | null) => void;
  setSelectedGlobalUserMappingID: (globalUserMappingID: number | null, skipNavigation?: boolean) => void;
  clients: any[];
  clientsLoading: boolean;
  clientsError: string | null;
  selectedClient: any | null;
  callerData: CallerData | null;
  refreshClients: () => Promise<void>;
  // Selected Invoices functionality
  selectedInvoices: ApiSelectedInvoicesData | null;
  selectedInvoicesLoading: boolean;
  selectedInvoicesError: string | null;
  refreshSelectedInvoices: () => void;
  updateSelectedInvoices: (invoices: ApiInvoiceItem[]) => void;
  clearSelectedInvoices: () => void;
  // Page Size functionality
  pageSize: number;
  pageSizeOptions: number[];
  setPageSize: (size: number) => void;
  // Client selector functionality
  shouldOpenClientSelector: boolean;
  setShouldOpenClientSelector: (open: boolean) => void;
  // Client logo functionality
  clientLogo: string | null;
  clientLogoLoading: boolean;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// Local storage key for persisting selected client across sessions (survives logout)
const SELECTED_CLIENT_STORAGE_KEY = 'selectedClient';
// Local storage key for persisting selected invoices per MappingID (unique across client DBs)
const SELECTED_INVOICES_KEY_PREFIX = 'selectedInvoices_mappingID_';
const SELECTED_INVOICES_KEY_PREFIX_LEGACY = 'selectedInvoices_contractorClientPersonID_';
// Local storage key for persisting page size preference
const PAGE_SIZE_STORAGE_KEY = 'pageSize';

// Helper functions for localStorage operations (changed from sessionStorage)
const getStoredClient = (): any | null => {
  try {
    const stored = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to read selected client from localStorage:', error);
    return null;
  }
};

const setStoredClient = (client: any | null): void => {
  try {
    if (client === null) {
      localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY);
    } else {
      // Store complete client object including all Client and Contractor fields
      const clientToStore = {
        // Core identification fields
        id: client.id,
        name: client.name,
        displayName: client.displayName,
        contractorName: client.contractorName,
        contractorId: client.contractorId,
        contractorEmail: client.contractorEmail,
        contractorPhone: client.contractorPhone,
        contractorClientPersonID: client.contractorClientPersonID,

        // Client address and contact information
        clientStreet: client.clientStreet,
        clientCity: client.clientCity,
        clientState: client.clientState,
        clientZipCode: client.clientZipCode,
        clientCountry: client.clientCountry,
        clientEmail: client.clientEmail,
        clientPhone: client.clientPhone,

        // Contractor address and contact information
        contractorStreet: client.contractorStreet,
        contractorCity: client.contractorCity,
        contractorState: client.contractorState,
        contractorZipCode: client.contractorZipCode,
        contractorCountry: client.contractorCountry,
        contractorType: client.contractorType,

        // Access control / permissions
        removeaccesslist: client.removeaccesslist,
        permissions: client.permissions,
        roleID: client.roleID,
        isActive: client.isActive,

        // Payment and culture settings
        achSurcharge: client.achSurcharge,
        achSurchargeCap: client.achSurchargeCap,
        achSurchargeIsVariable: client.achSurchargeIsVariable,
        creditCardSurcharge: client.creditCardSurcharge,
        cultureInfoName: client.cultureInfoName,
        currency: client.currency,

        globalUserMappingID: client.globalUserMappingID,
      };

      localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, JSON.stringify(clientToStore));
      // Refresh Pendo account identity when the selected client changes.
      initPendoFromSession();
      
    }
  } catch (error) {
    console.warn('Failed to save selected client to localStorage:', error);
  }
};

// Helper function to map home page Invoice objects to shopping cart ApiInvoiceItem format
const mapInvoiceToApiInvoiceItem = (invoice: any): ApiInvoiceItem => ({
  id: invoice.id || invoice.invoiceNumber,
  invoiceNumber: invoice.invoiceNumber || invoice.id,
  poNumber: invoice.poNumber || '',
  unitSent: invoice.unitSent || '',
  applyAmount: invoice.applyAmount || invoice.appliedAmount || 0,
  balance: invoice.balance || invoice.amount || invoice.openBalance || 0,
  jobDescription: invoice.jobDescription || invoice.description || invoice.name || '',
  invoiceDate: invoice.invoiceDate,
  dueDate: invoice.dueDate,
  message: invoice.message || invoice.comments || '',
  newBalance: invoice.newBalance,
  invoiceAmount: invoice.invoiceAmount || invoice.amount,
});

const getSelectedInvoicesKey = (globalUserMappingID: number | null): string => {
  return globalUserMappingID ? `${SELECTED_INVOICES_KEY_PREFIX}${globalUserMappingID}` : '';
};

const loadSelectedInvoices = (globalUserMappingID: number | null): ApiSelectedInvoicesData | null => {
  if (!globalUserMappingID) return null;
  
  try {
    const key = getSelectedInvoicesKey(globalUserMappingID);
    const stored = localStorage.getItem(key);
    if (stored) {
      const invoices: any[] = JSON.parse(stored);
      if (Array.isArray(invoices) && invoices.length > 0) {
        const mappedInvoices = invoices.map(mapInvoiceToApiInvoiceItem);
        const activeInvoices = mappedInvoices.filter((inv) => inv.balance > 0);

        if (activeInvoices.length !== mappedInvoices.length) {
          saveSelectedInvoices(globalUserMappingID, activeInvoices);
        }

        if (activeInvoices.length === 0) return null;

        const totalBalance = activeInvoices.reduce((sum, inv) => sum + inv.balance, 0);
        const totalApplyAmount = activeInvoices.reduce((sum, inv) => sum + inv.applyAmount, 0);
        
        return {
          invoices: activeInvoices,
          totalCount: activeInvoices.length,
          totalApplyAmount,
          totalBalance,
        };
      }
    }
  } catch (error) {
    console.error('Error parsing stored invoices for MappingID', globalUserMappingID, ':', error);
  }
  return null;
};

const saveSelectedInvoices = (globalUserMappingID: number | null, invoices: any[]): void => {
  if (!globalUserMappingID) return;
  
  try {
    const key = getSelectedInvoicesKey(globalUserMappingID);
    localStorage.setItem(key, JSON.stringify(invoices));
  } catch (error) {
    console.error('Error saving invoices for MappingID', globalUserMappingID, ':', error);
  }
};

const clearSelectedInvoicesForClient = (globalUserMappingID: number | null): void => {
  if (!globalUserMappingID) return;
  
  try {
    const key = getSelectedInvoicesKey(globalUserMappingID);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing invoices for MappingID', globalUserMappingID, ':', error);
  }
};

// Clean up problematic localStorage keys that shouldn't exist
const cleanupProblematicLocalStorageKeys = (): void => {
  try {
    // Remove the old 'selectedInvoices' key without a MappingID suffix
    localStorage.removeItem('selectedInvoices');
    
    // Remove empty-suffix keys and legacy person-ID prefix keys
    localStorage.removeItem(`${SELECTED_INVOICES_KEY_PREFIX}`);
    localStorage.removeItem(`${SELECTED_INVOICES_KEY_PREFIX_LEGACY}`);
    
    // Remove any keys with 'null' or 'undefined' as the key name
    localStorage.removeItem('null');
    localStorage.removeItem('undefined');
    
    // Clean up malformed suffix keys and leftover person-ID-based baskets
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith(SELECTED_INVOICES_KEY_PREFIX) && key.endsWith('_')) {
        keysToRemove.push(key);
      }
      if (key.startsWith(SELECTED_INVOICES_KEY_PREFIX_LEGACY)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (keysToRemove.length > 0) {
      console.log('Cleaned up problematic localStorage keys:', keysToRemove);
    }
  } catch (error) {
    console.error('Error cleaning up problematic localStorage keys:', error);
  }
};

// Helper functions for pageSize operations
const getStoredPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const pageSize = stored ? parseInt(stored, 10) : 10;
    // Validate that the pageSize is one of the allowed options
    const validOptions = [5, 10, 25, 50];
    return validOptions.includes(pageSize) ? pageSize : 10;
  } catch (error) {
    console.warn('Failed to read pageSize from localStorage:', error);
    return 10;
  }
};

const setStoredPageSize = (pageSize: number): void => {
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
  } catch (error) {
    console.warn('Failed to save pageSize to localStorage:', error);
  }
};

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClientContext must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [selectedClient, setSelectedClient] = useState<any | null>(getStoredClient);
  
  // Clean up problematic localStorage keys on initialization
  React.useEffect(() => {
    cleanupProblematicLocalStorageKeys();
  }, []);
  const [callerData, setCallerData] = useState<CallerData | null>(null);
  const {
    clients,
    loading: clientsLoading,
    error: clientsError,
    refreshClients: originalRefreshClients,
    isReady: isClientsSessionReady,
  } = useClients();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  // Store the previous MappingID for restoration after refresh (unique across client DBs)
  const [previousSelectedGlobalUserMappingID, setPreviousSelectedGlobalUserMappingID] = useState<number | null>(null);

  // Selected Invoices state
  const [selectedInvoices, setSelectedInvoices] = useState<ApiSelectedInvoicesData | null>(null);
  const [selectedInvoicesLoading, setSelectedInvoicesLoading] = useState(false);
  const [selectedInvoicesError, setSelectedInvoicesError] = useState<string | null>(null);

  // Page Size state
  const [pageSize, setPageSizeState] = useState<number>(getStoredPageSize);
  const pageSizeOptions = [5, 10, 25, 50];

  // Client selector state
  const [shouldOpenClientSelector, setShouldOpenClientSelector] = useState(false);

  // Client logo state
  const [clientLogo, setClientLogo] = useState<string | null>(null);
  const [clientLogoLoading, setClientLogoLoading] = useState(false);

  // Debug logging
  console.log('ClientProvider render:', {
    selectedClient: selectedClient
      ? {
          id: selectedClient.id,
          name: selectedClient.name,
          hasEmail: !!selectedClient.contractorEmail,
          hasPhone: !!selectedClient.contractorPhone,
          hasClientPersonID: !!selectedClient.contractorClientPersonID,
          email: selectedClient.contractorEmail,
          phone: selectedClient.contractorPhone,
          clientPersonID: selectedClient.contractorClientPersonID,
        }
      : null,
    callerData: callerData
      ? {
          name: `${callerData.FirstName} ${callerData.LastName}`,
          email: callerData.Email,
          phone: callerData.Phone,
        }
      : null,
    clientsCount: clients.length,
    clientsLoading,
  });

  // Extract selectedClientId from the selectedClient object
  const selectedClientId = selectedClient ? selectedClient.id : null;
  const selectedClientPersonID = selectedClient ? selectedClient.contractorClientPersonID : null;
  const selectedGlobalUserMappingID = selectedClient?.globalUserMappingID ?? null;
  const isClientSelectable = (client: any) => client && client.isActive !== false && client.isActive !== 0;

  const handleSetSelectedClientPersonID = (clientPrsonId: number | null) => {
    if (clientPrsonId === null) {
      setSelectedClient(null);
      setStoredClient(null);
    } else {
      const client = clients.find((client) => client.contractorClientPersonID === clientPrsonId && isClientSelectable(client));
      if (client) {
        setSelectedClient(client);
        setStoredClient(client);
        showSnackbar('Info', (
          <span>
            Selected client: <strong>{client.displayName}</strong>
          </span>
        ), 'info');
        navigate('/');
      }
    }
  };

  const handleSetSelectedGlobalUserMappingID = (globalUserMappingID: number | null, skipNavigation: boolean = false) => {
    if (globalUserMappingID === null) {
      setSelectedClient(null);
      setStoredClient(null);
    } else {
      const client = clients.find((client) => client.globalUserMappingID === globalUserMappingID && isClientSelectable(client));
      if (client) {
        setSelectedClient(client);
        setStoredClient(client);
        showSnackbar('Info', (
          <span>
            Selected client: <strong>{client.displayName}</strong>
          </span>
        ), 'info');
        if (!skipNavigation) {
          navigate('/');
        }
      }
    }
  };


  // Wrapped refreshClients that clears selected client and restores it after refresh
  const refreshClients = async () => {
    // Save MappingID before clearing - ClientPersonID is not unique across client DBs
    const currentMappingID = selectedClient?.globalUserMappingID ?? null;
    setPreviousSelectedGlobalUserMappingID(currentMappingID);
    
    // Clear the selected client before making the API call
    setSelectedClient(null);
    setStoredClient(null);
    
    // Call the original refresh function and wait for it
    await originalRefreshClients();
  };

  // Selected Invoices functions
  const refreshSelectedInvoices = () => {
    setSelectedInvoicesLoading(true);
    setSelectedInvoicesError(null);
    
    try {
      const data = loadSelectedInvoices(selectedGlobalUserMappingID);
      setSelectedInvoices(data);
    } catch (error) {
      setSelectedInvoicesError('Failed to refresh selected invoices');
      console.error('Error refreshing selected invoices:', error);
    } finally {
      setSelectedInvoicesLoading(false);
    }
  };

  const updateSelectedInvoices = (invoices: ApiInvoiceItem[]) => {
    try {
      const activeInvoices = invoices.filter((inv) => inv.balance > 0);

      // Convert ApiInvoiceItem back to the format expected by localStorage
      const invoicesForStorage = activeInvoices.map((invoice) => ({
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        poNumber: invoice.poNumber,
        unitSent: invoice.unitSent,
        applyAmount: invoice.applyAmount,
        balance: invoice.balance,
        jobDescription: invoice.jobDescription,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        comments: invoice.message,
        appliedAmount: invoice.applyAmount,
        amount: invoice.invoiceAmount || invoice.balance,
        openBalance: invoice.balance,
      }));

      // Save invoices for the current MappingID (unique across client DBs)
      saveSelectedInvoices(selectedGlobalUserMappingID, invoicesForStorage);
      
      // Update state
      const totalBalance = activeInvoices.reduce((sum, inv) => sum + inv.balance, 0);
      const totalApplyAmount = activeInvoices.reduce((sum, inv) => sum + inv.applyAmount, 0);
      
      setSelectedInvoices(activeInvoices.length > 0 ? {
        invoices: activeInvoices,
        totalCount: activeInvoices.length,
        totalApplyAmount,
        totalBalance,
      } : null);
      
      setSelectedInvoicesError(null);
    } catch (error) {
      setSelectedInvoicesError('Failed to update selected invoices');
      console.error('Error updating selected invoices:', error);
    }
  };

  const clearSelectedInvoices = () => {
    try {
      clearSelectedInvoicesForClient(selectedGlobalUserMappingID);
      cleanupProblematicLocalStorageKeys(); // Also clean up problematic keys
      setSelectedInvoices(null);
      setSelectedInvoicesError(null);
    } catch (error) {
      setSelectedInvoicesError('Failed to clear selected invoices');
      console.error('Error clearing selected invoices:', error);
    }
  };

  // Page Size functions
  const setPageSize = (size: number) => {
    // Validate that the pageSize is one of the allowed options
    if (pageSizeOptions.includes(size)) {
      setPageSizeState(size);
      setStoredPageSize(size);
    } else {
      console.warn('Invalid pageSize provided:', size);
    }
  };

  // Validate and refresh stored client when clients are loaded
  useEffect(() => {
    // Wait until session userID is resolved and the clients fetch/cache settle. Otherwise an
    // early empty list (userID not ready yet) used to wipe selectedClient and force company #1.
    if (!isClientsSessionReady || clientsLoading) {
      return;
    }

    const activeClients = clients.filter(isClientSelectable);

    // If clients finished loading but no active clients are available, clear the selected client
    if (activeClients.length === 0) {
      if (selectedClient) {
        console.log('No active clients available, clearing selected client');
        setSelectedClient(null);
        setStoredClient(null);
      }
      // Also clear the previous MappingID if set
      if (previousSelectedGlobalUserMappingID !== null) {
        setPreviousSelectedGlobalUserMappingID(null);
      }
      return;
    }

    if (activeClients.length > 0) {
      const findClientByStoredIdentity = (stored: { globalUserMappingID?: number; id?: number; contractorClientPersonID?: number }) => {
        if (stored.globalUserMappingID != null) {
          return activeClients.find((client) => client.globalUserMappingID === stored.globalUserMappingID);
        }
        // Legacy localStorage rows without MappingID: match company + person together
        return activeClients.find(
          (client) => client.id === stored.id && client.contractorClientPersonID === stored.contractorClientPersonID
        );
      };

      // Check if we need to restore a previously selected client after refresh
      if (previousSelectedGlobalUserMappingID !== null) {
        const previousClient = activeClients.find((client) => client.globalUserMappingID === previousSelectedGlobalUserMappingID);
        if (previousClient) {
          console.log('Restoring previously selected client after refresh:', previousClient.displayName);
          setSelectedClient(previousClient);
          setStoredClient(previousClient);
        } else {
          console.log('Previous client not found in refreshed active list, selecting first active client');
          setSelectedClient(activeClients[0]);
          setStoredClient(activeClients[0]);
        }
        // Clear the previous MappingID after restoration attempt
        setPreviousSelectedGlobalUserMappingID(null);
        return;
      }
      
      // If no selectedClient, automatically select the first one
      if (!selectedClient) {
        console.log('No selectedClient found, auto-selecting first active client');
        setSelectedClient(activeClients[0]);
        setStoredClient(activeClients[0]);
        return;
      }
      const freshClient = findClientByStoredIdentity(selectedClient);
      if (!freshClient) {
        // Stored client is invalid (deleted/inactive/missing); switch to first active client.
        console.warn(`Stored client ID ${selectedClient.id} not found in active clients. Selecting first active client.`);
        setSelectedClient(activeClients[0]);
        setStoredClient(activeClients[0]);
      } else {
        // Check if selectedClient needs refresh (missing any Client/Contractor fields or mismatched values)
        const areStringArraysEqual = (a?: string[], b?: string[]) => (a?.join('|') ?? '') === (b?.join('|') ?? '');
        const needsRefresh =
          !selectedClient.contractorEmail ||
          !selectedClient.contractorPhone ||
          selectedClient.contractorClientPersonID === undefined ||
          selectedClient.contractorEmail !== freshClient.contractorEmail ||
          selectedClient.contractorPhone !== freshClient.contractorPhone ||
          selectedClient.contractorClientPersonID !== freshClient.contractorClientPersonID ||
          // Check access control list
          !areStringArraysEqual(selectedClient.removeaccesslist, freshClient.removeaccesslist) ||
          // Check Client fields
          selectedClient.clientStreet !== freshClient.clientStreet ||
          selectedClient.clientCity !== freshClient.clientCity ||
          selectedClient.clientState !== freshClient.clientState ||
          selectedClient.clientZipCode !== freshClient.clientZipCode ||
          selectedClient.clientCountry !== freshClient.clientCountry ||
          selectedClient.clientEmail !== freshClient.clientEmail ||
          selectedClient.clientPhone !== freshClient.clientPhone ||
          // Check Contractor address fields
          selectedClient.contractorStreet !== freshClient.contractorStreet ||
          selectedClient.contractorCity !== freshClient.contractorCity ||
          selectedClient.contractorState !== freshClient.contractorState ||
          selectedClient.contractorZipCode !== freshClient.contractorZipCode ||
          selectedClient.contractorCountry !== freshClient.contractorCountry ||
          selectedClient.contractorType !== freshClient.contractorType;

        if (needsRefresh) {
          console.log('Refreshing selectedClient with fresh API data:', freshClient);
          setSelectedClient(freshClient);
          setStoredClient(freshClient);
        }
      }
    }
  }, [clients, clientsLoading, isClientsSessionReady, previousSelectedGlobalUserMappingID, selectedClient]);

  useEffect(() => {
    // Don't make caller API call if clients are still loading or no clients exist
    if (clientsLoading || clients.length === 0) {
      setCallerData(null);
      return;
    }

    const userID = getUserIdFromSession();

    if (selectedClient?.contractorClientPersonID && selectedClient?.id && userID) {
      console.log('Making caller API call...');
      axios.get(`users/${userID}/clients/${selectedClient.id}/callers/${selectedClient.contractorClientPersonID}`).then((response) => {
        setCallerData(response.data);
      });
    } else {
      setCallerData(null);
    }
  }, [selectedClient, clientsLoading, clients.length]);

  // Fetch client logo when selected client changes
  useEffect(() => {
    const fetchLogo = async () => {
      if (selectedClient?.id) {
        setClientLogoLoading(true);
        setClientLogo(null); // Clear previous logo while loading
        
        try {
          const logo = await fetchClientLogo(selectedClient.id, 'Base64');
          
          if (logo) {
            console.log(`Successfully fetched logo for client: ${selectedClient.id}`);
            setClientLogo(logo);
          } else {
            console.warn(`Failed to fetch logo for client: ${selectedClient.id}`);
            setClientLogo(null);
          }
        } catch (error) {
          console.error(`Error fetching logo for client: ${selectedClient.id}`, error);
          setClientLogo(null);
        } finally {
          setClientLogoLoading(false);
        }
      } else {
        // No client selected, clear logo
        setClientLogo(null);
        setClientLogoLoading(false);
      }
    };

    fetchLogo();
  }, [selectedClient]);

  React.useEffect(() => {
    if (clientsError) {
      showSnackbar('Error', clientsError, 'error');
    }
  }, [clientsError, showSnackbar]);

  useEffect(() => {
    refreshSelectedInvoices();
  }, []);

  useEffect(() => {
    if (selectedGlobalUserMappingID) {
      refreshSelectedInvoices();
    } else {
      setSelectedInvoices(null);
    }
  }, [selectedGlobalUserMappingID]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith(SELECTED_INVOICES_KEY_PREFIX)) {
        const currentClientKey = getSelectedInvoicesKey(selectedGlobalUserMappingID);
        if (e.key === currentClientKey) {
          refreshSelectedInvoices();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [selectedGlobalUserMappingID]);

  const value: ClientContextType = {
    selectedClientId,
    selectedClientPersonID,
    setSelectedClientPersonID: handleSetSelectedClientPersonID,
    setSelectedGlobalUserMappingID: handleSetSelectedGlobalUserMappingID,
    clients,
    clientsLoading,
    clientsError,
    selectedClient,
    callerData,
    refreshClients,
    // Selected Invoices functionality
    selectedInvoices,
    selectedInvoicesLoading,
    selectedInvoicesError,
    refreshSelectedInvoices,
    updateSelectedInvoices,
    clearSelectedInvoices,
    // Page Size functionality
    pageSize,
    pageSizeOptions,
    setPageSize,
    // Client selector functionality
    shouldOpenClientSelector,
    setShouldOpenClientSelector,
    // Client logo functionality
    clientLogo,
    clientLogoLoading,
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};
